import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
  Stack,
  Switch,
  TextField as MuiTextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckboxGroup from '../../checkBoxGroup';

interface QuestionOption {
  value: string;
  label: string;
}

interface QuestionDef {
  id: string;
  questionText: string;
  questionType: 'singleSelect' | 'multiSelect' | 'string' | 'boolean' | 'textarea' | 'number';
  required?: boolean;
  defaultValue?: any;
  options?: QuestionOption[];
}

interface SectionDef {
  id: string;
  title: string;
  questions: QuestionDef[];
}

interface Props {
  section: SectionDef;
  sectionIndex: number;
  totalSections: number;
  userResponses: Record<string, any>;
  maxHeight: number;
  onUserResponseChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
}

const StepDynamicSurvey: React.FC<Props> = ({
  section,
  sectionIndex,
  totalSections,
  userResponses,
  maxHeight,
  onUserResponseChange,
  onBack,
  onCancel,
  onNext,
}) => {
  const theme = useTheme();
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate default values for un-answered questions upon section load
  useEffect(() => {
    (section?.questions || []).forEach((q) => {
      if (userResponses[q.id] === undefined) {
        if (q.defaultValue !== undefined) {
          onUserResponseChange(q.id, q.defaultValue);
        } else if (q.questionType === 'boolean') {
          onUserResponseChange(q.id, false);
        }
      }
    });
  }, [section]);

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    let hasError = false;

    (section?.questions || []).forEach((q) => {
      if (q.required) {
        const val = userResponses[q.id] !== undefined ? userResponses[q.id] : q.defaultValue;
        if (q.questionType === 'boolean') {
          if (val === undefined || val === null) {
            newErrors[q.id] = 'This question is required.';
            hasError = true;
          }
        } else if (
          val === undefined ||
          val === null ||
          val === '' ||
          (Array.isArray(val) && val.length === 0)
        ) {
          newErrors[q.id] = 'This question is required.';
          hasError = true;
        }
      }
    });

    setErrors(newErrors);
    if (!hasError) {
      onNext();
    }
  };

  return (
    <Box
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `4px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Section Title Header */}
      <Box sx={{ p: 2.5, pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.secondary.main, 0.03) }}>
        <Typography variant="overline" sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: 0.8 }}>
          Section {sectionIndex + 1} of {totalSections}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
          {section?.title || 'Survey Section'}
        </Typography>
      </Box>

      {/* Questions Scrollable Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack spacing={3.5}>
          {(section?.questions || []).map((q) => {
            const errorMsg = errors[q.id];
            const currentVal = userResponses[q.id] !== undefined ? userResponses[q.id] : q.defaultValue;

            return (
              <Box key={q.id}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                  {q.questionText} {q.required && <span style={{ color: theme.palette.error.main }}>*</span>}
                </Typography>

                {/* Question Inputs by Type */}
                {q.questionType === 'singleSelect' && (
                  <MuiRadioGroup
                    value={currentVal || ''}
                    onChange={(e) => onUserResponseChange(q.id, e.target.value)}
                  >
                    {(q.options || []).map((opt) => (
                      <FormControlLabel
                        key={opt.value}
                        value={opt.value}
                        control={<Radio size="small" color="secondary" />}
                        label={<Typography variant="body2" sx={{ fontSize: '13.5px' }}>{opt.label}</Typography>}
                      />
                    ))}
                  </MuiRadioGroup>
                )}

                {q.questionType === 'multiSelect' && (
                  <CheckboxGroup
                    label=""
                    values={currentVal || []}
                    options={q.options || []}
                    onChange={(selectedVals) => onUserResponseChange(q.id, selectedVals)}
                  />
                )}

                {(q.questionType === 'string' || q.questionType === 'textarea' || q.questionType === 'number') && (
                  <MuiTextField
                    fullWidth
                    type={q.questionType === 'number' ? 'number' : 'text'}
                    multiline={q.questionType === 'textarea'}
                    rows={q.questionType === 'textarea' ? 3 : 1}
                    size="small"
                    placeholder="Enter your answer..."
                    value={currentVal !== undefined && currentVal !== null ? currentVal : ''}
                    onChange={(e) => onUserResponseChange(q.id, q.questionType === 'number' ? parseFloat(e.target.value) : e.target.value)}
                    error={Boolean(errorMsg)}
                    helperText={errorMsg}
                    sx={{ bgcolor: 'background.default', borderRadius: '8px' }}
                  />
                )}

                {q.questionType === 'boolean' && (
                  <FormControlLabel
                    control={
                      <Switch
                        color="secondary"
                        checked={Boolean(currentVal)}
                        onChange={(e) => onUserResponseChange(q.id, e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontSize: '13.5px' }}>{Boolean(currentVal) ? 'Enabled (Yes)' : 'Disabled (No)'}</Typography>}
                  />
                )}

                {errorMsg && q.questionType !== 'string' && q.questionType !== 'textarea' && q.questionType !== 'number' && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errorMsg}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* Action Bar Footer */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={1.5}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            Back
          </Button>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={onCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
              }}
            >
              Next Section
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepDynamicSurvey;
